import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguageNavigation } from "@/components/LanguageRouter";
import { blogPosts, getLocalizedField } from "@/data/blogPosts";

// Listing images (reuse the same map as Blog page)
import claudeCodeListing from "@/assets/blog/listing/claude-code-quality-listing.jpg";
import vendorlandListing from "@/assets/blog/listing/vendorland-listing.webp";
import tgiLlama33Listing from "@/assets/blog/listing/tgi-llama33-listing.webp";

const listingImages: Record<string, string> = {
  "claude-code-quality-hero.jpg": claudeCodeListing,
  "vendorland-recognition.png": vendorlandListing,
  "tgi-llama33-hero.png": tgiLlama33Listing,
};

export function RecentBlogSection() {
  const { t, i18n } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();
  const lang = i18n.language;
  const recentPosts = blogPosts.slice(0, 3);

  return (
    <section className="py-20 md:py-24 bg-muted">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {t("recentBlog.title")}
            </h2>
            <p className="mt-4 text-muted-foreground">
              {t("recentBlog.subtitle")}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {recentPosts.map((post) => (
              <Link
                key={post.slug}
                to={getLocalizedPath(`/blog/${post.slug}`)}
                className="group flex flex-col rounded-2xl bg-card border border-border/50 overflow-hidden transition-all duration-300 hover:border-primary/40 hover:shadow-lg hover:-translate-y-0.5"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={listingImages[post.heroImage]}
                    alt={getLocalizedField(post, "title", lang)}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-col flex-1 p-5">
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2 line-clamp-2">
                    {getLocalizedField(post, "title", lang)}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                    {getLocalizedField(post, "summary", lang)}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to={getLocalizedPath("/blog")}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              {t("recentBlog.viewAll")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
